'use client';

import { prefetchSocialAuthProviders } from '@eliadmin/core/src/components/auth/social-auth-providers-cache';
import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useWriteReview } from '../write/write-review';

/**
 * The parts of the reviews tab that depend on the session (`useWriteReview`). Until the customer
 * store knows the customer they render the signed-out variant – the usual visitor is a guest, and
 * a returning customer sees it only for the moment the session takes to load.
 */

/** Social sign-in providers load with the sign-in dialog: warm them up on intent (as the header). */
function useWarmUp(signedIn: boolean) {
  const locale = useLocaleStore(state => state.currentLocale);
  const warmUp = () => {
    if (!signedIn) void prefetchSocialAuthProviders(locale);
  };
  return { onFocus: warmUp, onMouseEnter: warmUp };
}

/** Figma „Button“ Outline M with the pencil (146:1374 header): the modal, or the sign-in first. */
export function WriteReviewButton({ label }: { label: string }) {
  const { write, signedIn } = useWriteReview();
  const warmUp = useWarmUp(signedIn);
  if (!label) return null;

  return (
    <Button
      variant="outline"
      size="md"
      iconLeft="pencil-simple"
      aria-haspopup="dialog"
      onClick={write}
      {...warmUp}
    >
      {label}
    </Button>
  );
}

/**
 * Figma „CTA · Prihlásenie“ (146:1374, signed out only – 146:3435 has none): user icon box, H5,
 * text, „Prihlásiť sa a hodnotiť“ (sign-in, then the modal), „Vytvoriť účet“ (core registration)
 * and the note about the e-mail after delivery. Not rendered from Figma: the sentence about the
 * verified-purchase badge (no such data).
 */
export function ReviewsLoginCard({
  slug,
  texts,
  className,
}: {
  /** The product – the registration returns to it (social sign-up). */
  slug: string;
  texts: { title: string; text: string; button: string; register: string; note: string };
  className?: string;
}) {
  const { write, signedIn } = useWriteReview();
  const warmUp = useWarmUp(signedIn);
  if (signedIn) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-[#E2E5DF] bg-white p-6',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-12 items-center justify-center rounded-xl bg-[#F6FBF7] text-[#127A26]"
      >
        <Icon name="user" size={24} />
      </span>
      {texts.title ? <h3 className="nt-h5 text-[#0D110D]">{texts.title}</h3> : null}
      {texts.text ? <p className="nt-body-m text-[#363D36]">{texts.text}</p> : null}
      <div className="flex flex-col gap-3">
        {texts.button ? (
          <Button
            variant="primary"
            size="md"
            aria-haspopup="dialog"
            onClick={write}
            className="w-full"
            {...warmUp}
          >
            {texts.button}
          </Button>
        ) : null}
        <Button asChild variant="outline" size="md" className="w-full">
          <Link href={ROUTES.REGISTER(ROUTES.PRODUCT({ slug }))} prefetch={false}>
            {texts.register}
          </Link>
        </Button>
      </div>
      {texts.note ? <p className="nt-caption text-[#5D655D]">{texts.note}</p> : null}
    </div>
  );
}

/**
 * Figma 146:2966 „Actions“ and the note under them: „Napísať prvé hodnotenie“ (the modal, or the
 * sign-in first) and, for guests, „Prihlásiť sa“ and the note that only signed-in customers review.
 */
export function EmptyStateActions({
  texts,
}: {
  texts: { first: string; signIn: string; note: string };
}) {
  const { write, signIn, signedIn } = useWriteReview();
  const warmUp = useWarmUp(signedIn);

  return (
    <>
      <div className="flex flex-wrap justify-center gap-3">
        {texts.first ? (
          <Button
            variant="primary"
            size="md"
            iconLeft="pencil-simple"
            aria-haspopup="dialog"
            onClick={write}
            {...warmUp}
          >
            {texts.first}
          </Button>
        ) : null}
        {signedIn ? null : (
          <Button
            variant="outline"
            size="md"
            iconLeft="user"
            aria-haspopup="dialog"
            onClick={signIn}
            {...warmUp}
          >
            {texts.signIn}
          </Button>
        )}
      </div>
      {texts.note && !signedIn ? (
        <p className="nt-caption max-w-[460px] text-[#5D655D]">{texts.note}</p>
      ) : null}
    </>
  );
}
